/**
 * WorkOrdersPanel (GP 1.0 · Tarea Oficial #1 · Iter 3)
 * ----------------------------------------------------
 * Listado global de Work Orders creadas desde el puente Invoice→Taller.
 * Igual look-and-feel que EstimatesPanel / InvoicesPanel / CreditNotesPanel.
 *
 * Sin información financiera. Solo datos de producción.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import OrderDetailsModal from "@/components/OrderDetailsModal";

const STATUS_LABEL_KEYS = {
  design: "wo_status_design",
  waiting_approval: "wo_status_waiting_approval",
  production: "wo_status_production",
  ready: "wo_status_ready",
  delivered: "wo_status_delivered",
};

export default function WorkOrdersPanel() {
  const { t } = useI18n();
  const STATUS_OPTIONS = [
    { value: "", label: t("filter_all_status_f") },
    { value: "design", label: t("wo_status_design") },
    { value: "waiting_approval", label: t("wo_status_waiting_approval") },
    { value: "production", label: t("wo_status_production") },
    { value: "ready", label: t("wo_status_ready") },
    { value: "delivered", label: t("wo_status_delivered") },
  ];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [employees, setEmployees] = useState([]);
  const [openOrder, setOpenOrder] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (status) params.set("status", status);
      const { data } = await axios.get(
        `${API}/commercial/work-orders?${params.toString()}`,
        { withCredentials: true }
      );
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toast.error(t("wo_panel_load_err"));
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/employees`, { withCredentials: true });
        setEmployees(Array.isArray(data) ? data : []);
      } catch {
        /* non-blocking */
      }
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 220);
    return () => clearTimeout(t);
  }, [load]);

  const openWo = async (row) => {
    try {
      const { data } = await axios.get(
        `${API}/commercial/work-orders/${row.id}`,
        { withCredentials: true }
      );
      setOpenOrder(data);
    } catch {
      toast.error(t("wo_panel_open_err"));
    }
  };

  const downloadPdf = async (row) => {
    try {
      const res = await axios.get(
        `${API}/commercial/work-orders/${row.id}/pdf`,
        { responseType: "blob", withCredentials: true }
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch {
      toast.error(t("wo_panel_pdf_err"));
    }
  };

  const totalItems = useMemo(
    () => rows.reduce((a, r) => a + (r.total_qty || 0), 0),
    [rows]
  );

  return (
    <div data-testid="work-orders-panel" className="space-y-6">
      <header>
        <div className="label-overline">DOC / WO</div>
        <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter">
          {t("wo_panel_title")}
        </h1>
        <p className="text-base text-[var(--text-secondary)] mt-1">
          {t("wo_panel_subtitle", {
            o: rows.length,
            eso: rows.length === 1 ? "" : "es",
            so: rows.length === 1 ? "" : "s",
            p: totalItems,
            sp: totalItems === 1 ? "" : "s",
          })}
        </p>
      </header>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          data-testid="wo-search-input"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("wo_panel_search_placeholder")}
          className="flex-1 px-3 py-2 border border-[var(--border)] bg-white text-sm font-mono-tech"
        />
        <select
          data-testid="wo-status-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 border border-[var(--border)] bg-white text-sm font-mono-tech"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <section className="bg-white border border-[var(--border)] overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-[var(--bg)]">
            <tr>
              <Th>{t("wo_panel_col_wo")}</Th>
              <Th>{t("wo_panel_col_origin")}</Th>
              <Th>{t("wo_panel_col_client")}</Th>
              <Th>{t("wo_panel_col_contact")}</Th>
              <Th>{t("wo_panel_col_delivery")}</Th>
              <Th className="text-right">{t("wo_panel_col_pieces")}</Th>
              <Th>{t("wo_panel_col_status")}</Th>
              <Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border)]">
            {loading && (
              <tr>
                <td colSpan={8} className="p-6 text-center text-sm text-[var(--text-secondary)]">
                  {t("loading_ellipsis")}
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="p-6 text-center text-sm text-[var(--text-secondary)]">
                  {t("wo_panel_empty")}
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`wo-row-${r.wo_number}`}
                  className="hover:bg-[var(--bg)] transition-colors cursor-pointer"
                  onClick={() => openWo(r)}
                >
                  <Td>
                    <span className="font-mono-tech text-sm font-bold">
                      {r.wo_number}
                    </span>
                  </Td>
                  <Td>
                    <span className="font-mono-tech text-xs text-[var(--text-secondary)]">
                      {r.source_invoice_number || "—"}
                    </span>
                  </Td>
                  <Td>
                    <div className="text-sm font-bold">{r.company_name || "—"}</div>
                  </Td>
                  <Td>
                    <div className="text-xs">
                      {r.contact_person || "—"}
                      {r.customer_phone ? ` · ${r.customer_phone}` : ""}
                    </div>
                  </Td>
                  <Td>
                    <div className="text-xs font-mono-tech">
                      {String(r.delivery_date || "").slice(0, 10) || "—"}
                    </div>
                  </Td>
                  <Td className="text-right font-mono-tech text-sm font-bold">
                    {r.total_qty || 0}
                  </Td>
                  <Td>
                    <span className="font-mono-tech text-[0.7rem] font-bold uppercase tracking-wider">
                      {STATUS_LABEL_KEYS[r.status] ? t(STATUS_LABEL_KEYS[r.status]) : (r.status || "—")}
                    </span>
                  </Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => downloadPdf(r)}
                      data-testid={`wo-pdf-${r.wo_number}`}
                      className="font-mono-tech text-[0.65rem] font-bold border border-[var(--border)] px-2 py-1 hover:bg-black hover:text-white transition-colors"
                    >
                      PDF
                    </button>
                  </Td>
                </tr>
              ))}
          </tbody>
        </table>
      </section>

      {openOrder && (
        <OrderDetailsModal
          open={Boolean(openOrder)}
          order={openOrder}
          employees={employees}
          onClose={() => setOpenOrder(null)}
          onChanged={() => {
            load();
          }}
        />
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-3 py-2 text-left font-mono-tech text-[0.7rem] font-bold tracking-[0.2em] text-[var(--text-secondary)] ${className}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className = "", onClick }) {
  return (
    <td className={`px-3 py-2 ${className}`} onClick={onClick}>
      {children}
    </td>
  );
}
